import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { Copy, Plus, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useUIStore } from '@/stores/uiStore';
import { copyRecipe, createRecipe, fetchRecipe, fetchRecipes, updateRecipe } from '@/lib/api/recipes';
import { cn } from '@/lib/utils';
import { useDebouncedSearch } from '@/hooks/useDebouncedSearch';
import type { Recipe, RecipeCategory } from '@/types/api';
import {
  RECIPE_CATEGORIES,
  RECIPE_CATEGORY_LABELS,
} from '@/types/api';
import { RecipeIngredientsSection } from '@/components/admin/RecipeIngredientsSection';

import { EditorLoadingPane } from './EditorLoadingPane';
import { LibraryPaginationFooter } from './LibraryPaginationFooter';
import { MobileEditorDrawer } from './MobileEditorDrawer';
import { useMediaQuery } from '@/hooks/useMediaQuery';

const recipeFormSchema = z.object({
  name: z.string().trim().min(1, 'Name is required').max(255),
  category: z.enum(RECIPE_CATEGORIES as [RecipeCategory, ...RecipeCategory[]]),
  description: z.string().max(5000).optional(),
  prepNotes: z.string().max(5000).optional(),
  smallToLargeRatioOverride: z.string().refine((value) => {
    if (!value) return true;
    const parsed = Number(value);
    return Number.isFinite(parsed) && parsed > 0 && parsed <= 1;
  }, 'S scale must be greater than 0 and at most 1').optional(),
  mediumToLargeRatioOverride: z.string().refine((value) => {
    if (!value) return true;
    const parsed = Number(value);
    return Number.isFinite(parsed) && parsed > 0 && parsed <= 1;
  }, 'M scale must be greater than 0 and at most 1').optional(),
  isActive: z.boolean(),
});

type RecipeFormValues = z.infer<typeof recipeFormSchema>;
type RecipeCategoryFilter = RecipeCategory | 'all';
const RECIPE_PAGE_SIZE = 12;

function parseScaleOverride(value: string | undefined) {
  if (!value) return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

interface LoadRecipesOptions {
  page?: number;
  search?: string;
  categoryFilter?: RecipeCategoryFilter;
  showInactive?: boolean;
}

function getUrlRecipeId(): number | null {
  if (typeof window === 'undefined') return null;
  const id = Number(new URLSearchParams(window.location.search).get('recipe'));
  return Number.isInteger(id) && id > 0 ? id : null;
}

export function RecipeLibrarySection() {
  const { showToast } = useUIStore();
  const [recipes, setRecipes] = useState<Recipe[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const { searchQuery, debouncedSearch, setSearchQuery, resetSearch } = useDebouncedSearch();
  const [categoryFilter, setCategoryFilter] = useState<RecipeCategoryFilter>('all');
  const [showInactive, setShowInactive] = useState(false);
  const [selectedRecipeId, setSelectedRecipeId] = useState<number | null>(null);
  const [selectedRecipeFallback, setSelectedRecipeFallback] = useState<Recipe | null>(null);
  const [creating, setCreating] = useState(false);
  const [copyingRecipe, setCopyingRecipe] = useState<Recipe | null>(null);
  const [mobileEditorOpen, setMobileEditorOpen] = useState(false);
  const [urlRecipePending, setUrlRecipePending] = useState(() => getUrlRecipeId() !== null);
  const selectedRecipeIdRef = useRef<number | null>(null);
  const latestRequestIdRef = useRef(0);
  const isDesktop = useMediaQuery('(min-width: 768px)');
  selectedRecipeIdRef.current = selectedRecipeId;

  const loadRecipes = useCallback(async (options: LoadRecipesOptions = {}) => {
    const nextPage = options.page ?? page;
    const nextSearch = options.search ?? debouncedSearch;
    const nextCategoryFilter = options.categoryFilter ?? categoryFilter;
    const nextShowInactive = options.showInactive ?? showInactive;

    const requestId = latestRequestIdRef.current + 1;
    latestRequestIdRef.current = requestId;

    try {
      setLoading(true);
      const response = await fetchRecipes({
        page: nextPage,
        pageSize: RECIPE_PAGE_SIZE,
        search: nextSearch || undefined,
        category: nextCategoryFilter !== 'all' ? nextCategoryFilter : undefined,
        activeOnly: !nextShowInactive,
        includeIngredientCount: true,
      });
      const responseTotalPages = Math.ceil(response.total / RECIPE_PAGE_SIZE);

      if (requestId !== latestRequestIdRef.current) {
        return [];
      }

      if (nextPage > 1 && responseTotalPages > 0 && nextPage > responseTotalPages) {
        setPage(responseTotalPages);
        return [];
      }

      setRecipes(response.recipes);
      setTotal(response.total);

      if (
        selectedRecipeIdRef.current
        && response.recipes.some((recipe) => recipe.id === selectedRecipeIdRef.current)
      ) {
        setSelectedRecipeFallback(null);
      }
      return response.recipes;
    } catch (error) {
      if (requestId !== latestRequestIdRef.current) {
        return [];
      }

      showToast(
        error instanceof Error ? error.message : 'Failed to load recipes',
        'error'
      );
      return [];
    } finally {
      if (requestId === latestRequestIdRef.current) {
        setLoading(false);
      }
    }
  }, [categoryFilter, debouncedSearch, page, showInactive, showToast]);

  useEffect(() => {
    void loadRecipes();
  }, [loadRecipes]);

  useEffect(() => {
    setPage(1);
  }, [categoryFilter, debouncedSearch, showInactive]);

  // Mount-only: open the recipe named by ?recipe= (e.g. from a procurement guardrail dialog).
  useEffect(() => {
    const id = getUrlRecipeId();
    if (id === null) return;

    setSelectedRecipeId(id);
    if (!isDesktop) setMobileEditorOpen(true);
    fetchRecipe(id)
      .then(setSelectedRecipeFallback)
      .catch(() => {
        showToast('Recipe not found', 'error');
        setMobileEditorOpen(false);
      })
      .finally(() => setUrlRecipePending(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isDesktop && mobileEditorOpen) {
      setMobileEditorOpen(false);
    }
  }, [isDesktop, mobileEditorOpen]);

  useEffect(() => {
    if (creating || urlRecipePending) return;

    if (recipes.length === 0) {
      if (loading) return;
      setSelectedRecipeId(null);
      setSelectedRecipeFallback(null);
      setMobileEditorOpen(false);
      return;
    }

    const hasSelectionOnPage = selectedRecipeId
      ? recipes.some((recipe) => recipe.id === selectedRecipeId)
      : false;
    const hasFallbackSelection = selectedRecipeFallback?.id === selectedRecipeId;

    if (!selectedRecipeId || (!hasSelectionOnPage && !hasFallbackSelection)) {
      const firstRecipe = recipes[0];
      if (firstRecipe) {
        setSelectedRecipeId(firstRecipe.id);
      }
    }
  }, [creating, loading, recipes, selectedRecipeFallback, selectedRecipeId, urlRecipePending]);

  const selectedRecipe = useMemo(
    () =>
      recipes.find((recipe) => recipe.id === selectedRecipeId)
      ?? (selectedRecipeFallback?.id === selectedRecipeId ? selectedRecipeFallback : null),
    [recipes, selectedRecipeFallback, selectedRecipeId]
  );

  const startCreate = () => {
    setCreating(true);
    setSelectedRecipeId(null);
    setSelectedRecipeFallback(null);
    setMobileEditorOpen(true);
  };

  const startCopy = (recipe: Recipe) => {
    setCopyingRecipe(recipe);
  };

  const selectRecipe = (recipeId: number) => {
    setCreating(false);
    setSelectedRecipeId(recipeId);
    setSelectedRecipeFallback(null);
    setMobileEditorOpen(true);
  };

  const closeEditor = () => {
    setCreating(false);
    setMobileEditorOpen(false);
  };

  const handleRecipeSaved = async (savedRecipe: Recipe, mode: 'create' | 'edit') => {
    const recipeWithLibraryMetadata: Recipe = {
      ...savedRecipe,
      ingredientCount: savedRecipe.ingredientCount ?? (mode === 'create' ? 0 : selectedRecipe?.ingredientCount ?? 0),
    };

    if (mode === 'create') {
      resetSearch();
      setCategoryFilter('all');
      setShowInactive(false);
      setCreating(false);
      setPage(1);
      const refreshedRecipes = await loadRecipes({
        page: 1,
        search: '',
        categoryFilter: 'all',
        showInactive: false,
      });
      setSelectedRecipeId(savedRecipe.id);
      setSelectedRecipeFallback(
        refreshedRecipes.some((recipe) => recipe.id === savedRecipe.id)
          ? null
          : recipeWithLibraryMetadata
      );
    } else {
      const refreshedRecipes = await loadRecipes();
      setSelectedRecipeId(savedRecipe.id);
      setSelectedRecipeFallback(
        refreshedRecipes.some((recipe) => recipe.id === savedRecipe.id)
          ? null
          : recipeWithLibraryMetadata
      );
    }
    setMobileEditorOpen(true);
  };

  const handleRecipeCopied = async (copiedRecipe: Recipe) => {
    setCopyingRecipe(null);
    await handleRecipeSaved(copiedRecipe, 'create');
  };

  const handleIngredientCountChange = useCallback((recipeId: number, count: number) => {
    setRecipes((previous) =>
      previous.map((recipe) => {
        if (recipe.id !== recipeId || recipe.ingredientCount === count) {
          return recipe;
        }

        return { ...recipe, ingredientCount: count };
      })
    );

    setSelectedRecipeFallback((previous) => {
      if (!previous || previous.id !== recipeId || previous.ingredientCount === count) {
        return previous;
      }

      return { ...previous, ingredientCount: count };
    });
  }, []);

  const recipeCountLabel = loading ? 'Loading recipes...' : `${total} recipe${total === 1 ? '' : 's'}`;
  const shouldShowLoadingPane = !creating && !selectedRecipe && (loading || recipes.length > 0);
  const mobileEditorTitle = creating ? 'Create Recipe' : selectedRecipe?.name ?? 'Recipe details';
  const editorPane = shouldShowLoadingPane ? (
    <EditorLoadingPane
      title="Loading recipe"
      description="Fetching the first recipe for this view."
    />
  ) : (
    <RecipeEditorPane
      recipe={creating ? null : selectedRecipe}
      onSaved={handleRecipeSaved}
      onIngredientCountChange={handleIngredientCountChange}
      showHeader={isDesktop}
    />
  );

  return (
    <>
      <div className="grid gap-4 md:grid-cols-[minmax(260px,2fr)_minmax(200px,3fr)]">
        <Card className="flex min-h-[620px]">
          <CardHeader className="space-y-4 border-b">
            <div className="flex items-start justify-between gap-4">
              <div>
                <CardTitle>Recipes</CardTitle>
                <CardDescription>{recipeCountLabel}</CardDescription>
              </div>
              <Button size="sm" onClick={startCreate}>
                <Plus className="h-4 w-4" />
                New
              </Button>
            </div>

            <div className="space-y-3">
              <div className="relative">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  placeholder="Search recipes..."
                  value={searchQuery}
                  onChange={(event) => setSearchQuery(event.target.value)}
                  className="pl-9"
                />
              </div>

              <div className="grid grid-cols-[minmax(0,1fr)_auto] gap-3">
                <Select
                  value={categoryFilter}
                  onValueChange={(value) => setCategoryFilter(value as RecipeCategoryFilter)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="All categories" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Categories</SelectItem>
                    {RECIPE_CATEGORIES.map((category) => (
                      <SelectItem key={category} value={category}>
                        {RECIPE_CATEGORY_LABELS[category]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <label className="flex items-center gap-2 rounded-md border px-3 text-sm">
                  <input
                    type="checkbox"
                    checked={showInactive}
                    onChange={(event) => setShowInactive(event.target.checked)}
                    className="h-4 w-4 rounded border-gray-300"
                  />
                  Inactive
                </label>
              </div>
            </div>
          </CardHeader>

          <CardContent className="flex-1 overflow-y-auto px-0">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="h-10 w-10 animate-spin rounded-full border-b-2 border-primary" />
              </div>
            ) : recipes.length === 0 ? (
              <div className="px-6 py-12 text-center">
                <p className="mb-4 text-sm text-muted-foreground">
                  {debouncedSearch ? `No recipes found matching "${debouncedSearch}"` : 'No recipes found'}
                </p>
                <Button size="sm" onClick={startCreate}>Create First Recipe</Button>
              </div>
            ) : (
              <div className="px-4 py-3">
                <div className="mb-2 grid grid-cols-[minmax(0,1fr)_112px_36px] gap-3 px-2 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                  <span>Name</span>
                  <span>Category</span>
                  <span className="sr-only">Actions</span>
                </div>

                <div className="space-y-2" role="listbox" aria-label="Recipes">
                  {recipes.map((recipe) => {
                    const isSelected = !creating && selectedRecipeId === recipe.id;

                    return (
                      <div
                        key={recipe.id}
                        role="option"
                        aria-selected={isSelected}
                        className={cn(
                          'grid grid-cols-[minmax(0,1fr)_112px_36px] items-center gap-3 rounded-lg border px-2 py-2 transition-colors',
                          isSelected ? 'border-primary/40 bg-primary/5' : 'hover:bg-muted/60',
                          recipe.isActive ? '' : 'opacity-60'
                        )}
                      >
                        <button
                          type="button"
                          className="min-w-0 text-left"
                          onClick={() => selectRecipe(recipe.id)}
                        >
                          <p className="truncate font-medium text-foreground" title={recipe.name}>
                            {recipe.name}
                          </p>
                        </button>

                        <div className="truncate text-sm text-muted-foreground" title={RECIPE_CATEGORY_LABELS[recipe.category]}>
                          {RECIPE_CATEGORY_LABELS[recipe.category]}
                        </div>

                        <Button
                          type="button"
                          variant="ghost"
                          size="icon-sm"
                          aria-label={`Copy ${recipe.name}`}
                          title={`Copy ${recipe.name}`}
                          onClick={() => startCopy(recipe)}
                        >
                          <Copy className="h-4 w-4" />
                        </Button>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </CardContent>

          <LibraryPaginationFooter
            page={page}
            pageSize={RECIPE_PAGE_SIZE}
            total={total}
            loading={loading}
            onPageChange={setPage}
          />
        </Card>

        {isDesktop ? editorPane : null}

        {!isDesktop ? (
          <MobileEditorDrawer
            open={mobileEditorOpen}
            onOpenChange={setMobileEditorOpen}
            onClose={closeEditor}
            title={mobileEditorTitle}
          >
            {editorPane}
          </MobileEditorDrawer>
        ) : null}
      </div>

      <RecipeCopyDialog
        recipe={copyingRecipe}
        open={!!copyingRecipe}
        isDesktop={isDesktop}
        onOpenChange={(open) => {
          if (!open) setCopyingRecipe(null);
        }}
        onCopied={handleRecipeCopied}
      />
    </>
  );
}

interface RecipeEditorPaneProps {
  recipe: Recipe | null;
  onSaved: (recipe: Recipe, mode: 'create' | 'edit') => Promise<void>;
  onIngredientCountChange?: (recipeId: number, count: number) => void;
  showHeader?: boolean;
}

function RecipeEditorPane({
  recipe,
  onSaved,
  onIngredientCountChange,
  showHeader = true,
}: RecipeEditorPaneProps) {
  const { showToast } = useUIStore();
  const [saving, setSaving] = useState(false);
  const isEditMode = !!recipe;

  const {
    handleSubmit,
    register,
    reset,
    setValue,
    watch,
    formState: { errors },
  } = useForm<RecipeFormValues>({
    resolver: zodResolver(recipeFormSchema),
    defaultValues: buildRecipeDefaults(recipe),
  });

  useEffect(() => {
    reset(buildRecipeDefaults(recipe));
  }, [recipe, reset]);

  const categoryValue = watch('category');
  const isActive = watch('isActive');

  const onSubmit = async (values: RecipeFormValues) => {
    try {
      setSaving(true);

      const payload = {
        name: values.name.trim(),
        category: values.category,
        description: values.description?.trim() || null,
        prepNotes: values.prepNotes?.trim() || null,
        smallToLargeRatioOverride: parseScaleOverride(values.smallToLargeRatioOverride),
        mediumToLargeRatioOverride: parseScaleOverride(values.mediumToLargeRatioOverride),
        ...(isEditMode ? { isActive: values.isActive } : {}),
      };

      const savedRecipe = isEditMode && recipe
        ? await updateRecipe(recipe.id, payload)
        : await createRecipe(payload);

      showToast(
        isEditMode ? 'Recipe updated successfully' : 'Recipe created successfully',
        'success'
      );

      await onSaved(savedRecipe, isEditMode ? 'edit' : 'create');
    } catch (error) {
      showToast(
        error instanceof Error ? error.message : `Failed to ${isEditMode ? 'update' : 'create'} recipe`,
        'error'
      );
    } finally {
      setSaving(false);
    }
  };

  const handleCountChange = useCallback((count: number) => {
    if (!recipe?.id) return;
    onIngredientCountChange?.(recipe.id, count);
  }, [onIngredientCountChange, recipe?.id]);

  return (
    <Card className="min-h-[620px]">
      {showHeader ? (
        <CardHeader className="border-b">
          <CardTitle>{isEditMode ? recipe.name : 'Create Recipe'}</CardTitle>
        </CardHeader>
      ) : null}

      <CardContent className="flex-1 overflow-y-auto">
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          <div className="grid gap-4 sm:grid-cols-[minmax(0,1fr)_220px]">
            <div className="space-y-2">
              <Label htmlFor="recipe-name">Recipe Name</Label>
              <Input
                id="recipe-name"
                {...register('name')}
                placeholder="e.g. Chicken Biryani"
                disabled={saving}
              />
              {errors.name ? <p className="text-sm text-destructive">{errors.name.message}</p> : null}
            </div>

            <div className="space-y-2">
              <Label htmlFor="recipe-category">Category</Label>
              <Select
                value={categoryValue}
                onValueChange={(value) => setValue('category', value as RecipeCategory, { shouldDirty: true })}
                disabled={saving}
              >
                <SelectTrigger id="recipe-category">
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  {RECIPE_CATEGORIES.map((category) => (
                    <SelectItem key={category} value={category}>
                      {RECIPE_CATEGORY_LABELS[category]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors.category ? <p className="text-sm text-destructive">{errors.category.message}</p> : null}
            </div>
          </div>

          <div className="grid gap-3 sm:grid-cols-3">
            <div className="space-y-2">
              <Label htmlFor="recipe-small-scale">S Scale</Label>
              <Input
                id="recipe-small-scale"
                type="number"
                step="any"
                min="0"
                max="1"
                {...register('smallToLargeRatioOverride')}
                placeholder="0.5 default"
                disabled={saving}
              />
              {errors.smallToLargeRatioOverride ? (
                <p className="text-sm text-destructive">{errors.smallToLargeRatioOverride.message}</p>
              ) : null}
            </div>

            <div className="space-y-2">
              <Label htmlFor="recipe-medium-scale">M Scale</Label>
              <Input
                id="recipe-medium-scale"
                type="number"
                step="any"
                min="0"
                max="1"
                {...register('mediumToLargeRatioOverride')}
                placeholder="0.75 default"
                disabled={saving}
              />
              {errors.mediumToLargeRatioOverride ? (
                <p className="text-sm text-destructive">{errors.mediumToLargeRatioOverride.message}</p>
              ) : null}
            </div>

          </div>

          <div className="space-y-2">
            <Label htmlFor="recipe-description">Description</Label>
            <Textarea
              id="recipe-description"
              {...register('description')}
              placeholder="What should kitchen staff know about this dish?"
              rows={3}
              disabled={saving}
            />
            {errors.description ? <p className="text-sm text-destructive">{errors.description.message}</p> : null}
          </div>

          <div className="space-y-2">
            <Label htmlFor="recipe-prep-notes">Preparation Notes</Label>
            <Textarea
              id="recipe-prep-notes"
              {...register('prepNotes')}
              placeholder="Cooking instructions, timing, service notes..."
              rows={5}
              disabled={saving}
            />
            {errors.prepNotes ? <p className="text-sm text-destructive">{errors.prepNotes.message}</p> : null}
          </div>

          <div className="rounded-lg border bg-muted/30 px-4 py-3">
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={isActive}
                onChange={(event) => setValue('isActive', event.target.checked, { shouldDirty: true })}
                className="h-4 w-4 rounded border-gray-300"
                disabled={saving}
              />
              Active and selectable for future menus
            </label>
          </div>

          <div className="flex items-center justify-end gap-2 border-t pt-4">
            <Button type="submit" disabled={saving}>
              {saving ? 'Saving...' : isEditMode ? 'Save Changes' : 'Create Recipe'}
            </Button>
          </div>
        </form>

        {recipe ? (
          <div className="mt-6 border-t pt-6">
            <RecipeIngredientsSection
              recipeId={recipe.id}
              onCountChange={handleCountChange}
            />
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}

interface RecipeCopyDialogProps {
  recipe: Recipe | null;
  open: boolean;
  isDesktop: boolean;
  onOpenChange: (open: boolean) => void;
  onCopied: (recipe: Recipe) => Promise<void>;
}

function RecipeCopyDialog({
  recipe,
  open,
  isDesktop,
  onOpenChange,
  onCopied,
}: RecipeCopyDialogProps) {
  const { showToast } = useUIStore();
  const [copying, setCopying] = useState(false);

  const {
    handleSubmit,
    register,
    reset,
    setValue,
    watch,
    formState: { errors },
  } = useForm<RecipeFormValues>({
    resolver: zodResolver(recipeFormSchema),
    defaultValues: buildRecipeCopyDefaults(recipe),
  });

  useEffect(() => {
    if (open) {
      reset(buildRecipeCopyDefaults(recipe));
    }
  }, [open, recipe, reset]);

  const categoryValue = watch('category');
  const isActive = watch('isActive');
  const ingredientCount = recipe?.ingredientCount ?? 0;
  const description = `Create an active copy of ${recipe?.name ?? 'this recipe'} with ${ingredientCount} copied ingredient${ingredientCount === 1 ? '' : 's'}.`;

  const onSubmit = async (values: RecipeFormValues) => {
    if (!recipe) return;

    try {
      setCopying(true);
      const copiedRecipe = await copyRecipe(recipe.id, {
        name: values.name.trim(),
        category: values.category,
        description: values.description?.trim() || null,
        prepNotes: values.prepNotes?.trim() || null,
        smallToLargeRatioOverride: parseScaleOverride(values.smallToLargeRatioOverride),
        mediumToLargeRatioOverride: parseScaleOverride(values.mediumToLargeRatioOverride),
        isActive: values.isActive,
      });

      showToast('Recipe copied successfully', 'success');
      await onCopied(copiedRecipe);
    } catch (error) {
      showToast(
        error instanceof Error ? error.message : 'Failed to copy recipe',
        'error'
      );
    } finally {
      setCopying(false);
    }
  };

  const form = (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
      {!isDesktop ? <p className="text-sm text-muted-foreground">{description}</p> : null}

      <div className="space-y-2">
        <Label htmlFor="copy-recipe-name">New Recipe Name</Label>
        <Input
          id="copy-recipe-name"
          {...register('name')}
          disabled={copying}
        />
        {errors.name ? <p className="text-sm text-destructive">{errors.name.message}</p> : null}
      </div>

      <div className="space-y-2">
        <Label htmlFor="copy-recipe-category">Category</Label>
        <Select
          value={categoryValue}
          onValueChange={(value) => setValue('category', value as RecipeCategory, { shouldDirty: true })}
          disabled={copying}
        >
          <SelectTrigger id="copy-recipe-category">
            <SelectValue placeholder="Select category" />
          </SelectTrigger>
          <SelectContent>
            {RECIPE_CATEGORIES.map((category) => (
              <SelectItem key={category} value={category}>
                {RECIPE_CATEGORY_LABELS[category]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {errors.category ? <p className="text-sm text-destructive">{errors.category.message}</p> : null}
      </div>

      <div className="grid gap-3 sm:grid-cols-3">
        <div className="space-y-2">
          <Label htmlFor="copy-recipe-small-scale">S Scale</Label>
          <Input
            id="copy-recipe-small-scale"
            type="number"
            step="any"
            min="0"
            max="1"
            {...register('smallToLargeRatioOverride')}
            placeholder="0.5 default"
            disabled={copying}
          />
          {errors.smallToLargeRatioOverride ? (
            <p className="text-sm text-destructive">{errors.smallToLargeRatioOverride.message}</p>
          ) : null}
        </div>

        <div className="space-y-2">
          <Label htmlFor="copy-recipe-medium-scale">M Scale</Label>
          <Input
            id="copy-recipe-medium-scale"
            type="number"
            step="any"
            min="0"
            max="1"
            {...register('mediumToLargeRatioOverride')}
            placeholder="0.75 default"
            disabled={copying}
          />
          {errors.mediumToLargeRatioOverride ? (
            <p className="text-sm text-destructive">{errors.mediumToLargeRatioOverride.message}</p>
          ) : null}
        </div>

      </div>

      <div className="space-y-2">
        <Label htmlFor="copy-recipe-description">Description</Label>
        <Textarea
          id="copy-recipe-description"
          {...register('description')}
          rows={3}
          disabled={copying}
        />
        {errors.description ? <p className="text-sm text-destructive">{errors.description.message}</p> : null}
      </div>

      <div className="space-y-2">
        <Label htmlFor="copy-recipe-prep-notes">Preparation Notes</Label>
        <Textarea
          id="copy-recipe-prep-notes"
          {...register('prepNotes')}
          rows={4}
          disabled={copying}
        />
        {errors.prepNotes ? <p className="text-sm text-destructive">{errors.prepNotes.message}</p> : null}
      </div>

      <div className="rounded-lg border bg-muted/30 px-4 py-3">
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={isActive}
            onChange={(event) => setValue('isActive', event.target.checked, { shouldDirty: true })}
            className="h-4 w-4 rounded border-gray-300"
            disabled={copying}
          />
          Active and selectable for future menus
        </label>
      </div>

      <DialogFooter className={isDesktop ? undefined : 'border-t pt-4'}>
        <Button
          type="button"
          variant="outline"
          onClick={() => onOpenChange(false)}
          disabled={copying}
        >
          Cancel
        </Button>
        <Button type="submit" disabled={copying}>
          {copying ? 'Creating Copy...' : 'Create Copy'}
        </Button>
      </DialogFooter>
    </form>
  );

  if (!isDesktop) {
    return (
      <MobileEditorDrawer
        open={open}
        onOpenChange={onOpenChange}
        onClose={() => onOpenChange(false)}
        title="Copy Recipe"
      >
        {form}
      </MobileEditorDrawer>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[560px]">
        <DialogHeader>
          <DialogTitle>Copy Recipe</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        {form}
      </DialogContent>
    </Dialog>
  );
}

function buildRecipeDefaults(recipe: Recipe | null): RecipeFormValues {
  return {
    name: recipe?.name ?? '',
    category: recipe?.category ?? 'CHICKEN_ITEM',
    description: recipe?.description ?? '',
    prepNotes: recipe?.prepNotes ?? '',
    smallToLargeRatioOverride: recipe?.smallToLargeRatioOverride ?? '',
    mediumToLargeRatioOverride: recipe?.mediumToLargeRatioOverride ?? '',
    isActive: recipe?.isActive ?? true,
  };
}

function buildRecipeCopyDefaults(recipe: Recipe | null): RecipeFormValues {
  return {
    name: recipe ? `Copy of ${recipe.name}` : '',
    category: recipe?.category ?? 'CHICKEN_ITEM',
    description: recipe?.description ?? '',
    prepNotes: recipe?.prepNotes ?? '',
    smallToLargeRatioOverride: recipe?.smallToLargeRatioOverride ?? '',
    mediumToLargeRatioOverride: recipe?.mediumToLargeRatioOverride ?? '',
    isActive: true,
  };
}
